import { ExportOutlined } from "@ant-design/icons";
import { priorityLabels } from "@p0security/shared/assessment/constants";
import { toAssessmentPath } from "@p0security/shared/assessment/helper";
import { providerLabels } from "@p0security/shared/assessment/render";
import { JiraIntegration } from "@p0security/shared/integrations/trackers/jira/types";
import { AppPaths } from "@p0security/shared/routes/constants";
import {
  ALL_SCOPE_SENTINEL,
  AssessmentScopeIntegration,
  Provider,
  scopeToProvider,
} from "@p0security/shared/types/assessment";
import { TargetNodeType } from "@p0security/shared/types/assessment/data/index";
import {
  MonitorScope,
  SavedMonitor,
  monitorPriorities,
} from "@p0security/shared/types/assessment/monitor";
import { isDefined } from "@p0security/shared/types/is";
import {
  Button,
  Divider,
  Flex,
  Form,
  Input,
  Select,
  Skeleton,
  Switch,
  Typography,
  message,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import { SelectedEnvironmentContext } from "components/Environment/contexts/SelectedEnvironmentContext";
import { GraphTooltip } from "components/GraphTooltip";
import { JiraIcon } from "components/Integrations/Jira/Jira";
import { Tenant } from "components/Login";
import {
  addDoc,
  collection,
  deleteField,
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { pickBy, uniq } from "lodash";
import { DB, useFirestoreDoc } from "providers/FirestoreProvider";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { MonitorWithMetadata } from "../../contexts/FindingsContext";
import { useNavigateWithEnv } from "../../hooks/useNavigateWithEnv";
import { targetLogo } from "../TargetLogo";
import { validateSearchTerm } from "./types";

interface EditMonitorFormProps {
  /** When true, hides the search term and label fields in the form */
  hideSearchAndLabel?: boolean;
  initialSearchTerm?: string;
  isCustom: boolean;
  monitor?: MonitorWithMetadata;
  /** Template values to pre-populate form when creating from template */
  templateValues?: Partial<MonitorWithMetadata>;
  /** Unique key to detect when a new template selection is applied */
  templateKey?: string;
  /** Optional callback invoked whenever the monitor label changes. */
  onMonitorNameChange?: (label: string) => void;
  onSearchTermChange?: (searchTerm: string) => void;
  onSubmitForm: () => void;
  show: TargetNodeType;
}

export const EditMonitorForm: React.FC<EditMonitorFormProps> = ({
  hideSearchAndLabel,
  initialSearchTerm,
  isCustom,
  monitor,
  templateValues,
  templateKey,
  onMonitorNameChange,
  onSearchTermChange,
  onSubmitForm,
  show,
}) => {
  const navigate = useNavigateWithEnv();
  const tenantId = useContext(Tenant);
  const { assessment, details } = useContext(SelectedEnvironmentContext);
  const assessmentId = details?.assessmentId;
  type MonitorFormData = SavedMonitor & { autoAssign?: boolean };
  const [form] = Form.useForm<MonitorFormData>();
  const [saving, setSaving] = useState(false);
  const lastTemplateKeyRef = useRef<string | undefined>();

  useEffect(() => {
    if (!templateKey) {
      lastTemplateKeyRef.current = undefined;
    }
  }, [templateKey]);

  const { doc: jiraDoc, loading: jiraLoading } =
    useFirestoreDoc<JiraIntegration>(`integrations/jira`, {
      live: true,
      tenantAware: true,
    });

  const monitorScopes = useMemo(
    () => [
      ALL_SCOPE_SENTINEL,
      ...(assessment.doc
        ? uniq(assessment.doc.data.targets.map((scope) => scope.integration))
        : []),
    ],
    [assessment.doc]
  );

  const getTargetLogo = useCallback(
    (integration: AssessmentScopeIntegration): React.ReactNode =>
      targetLogo(integration),
    []
  );

  const integrationLabelFor = useCallback(
    (integration: MonitorScope): string => {
      if (integration === ALL_SCOPE_SENTINEL) return "All targets";
      return providerLabels[scopeToProvider(integration) as Provider];
    },
    []
  );

  const monitorContextLabel = useMemo(() => {
    const labels: Record<TargetNodeType, string> = {
      credential: "Credential",
      identity: "Identity",
      grant: "Grant",
      resource: "Resource",
    };
    return templateValues?.show
      ? labels[templateValues.show]
      : (labels[show] ?? show.charAt(0).toUpperCase() + show.slice(1));
  }, [show, templateValues?.show]);

  useEffect(() => {
    if (monitor !== undefined) {
      form.setFieldsValue({
        label: monitor.label,
        description: monitor.description ?? "",
        priority: monitor.priority,
        searchTerm: monitor.searchTerm ?? "",
        scopes: monitor.scopes,
        autoAssign: monitor.autoAssign ?? false,
      });
      onMonitorNameChange?.(monitor.label);
      lastTemplateKeyRef.current = undefined;
      return;
    }

    if (initialSearchTerm) {
      form.setFieldsValue({
        searchTerm: initialSearchTerm,
      });
      onSearchTermChange?.(initialSearchTerm);
    }

    if (templateValues && templateKey) {
      const shouldApplyTemplate = lastTemplateKeyRef.current !== templateKey;

      if (shouldApplyTemplate) {
        form.setFieldsValue({
          label: templateValues.label,
          description: templateValues.description ?? "",
          priority: templateValues.priority ?? "MEDIUM",
          searchTerm: templateValues.searchTerm ?? initialSearchTerm ?? "",
          scopes: templateValues.scopes ?? [ALL_SCOPE_SENTINEL],
          autoAssign: templateValues.autoAssign ?? false,
        });
        if (templateValues.label) {
          onMonitorNameChange?.(templateValues.label);
        }
        lastTemplateKeyRef.current = templateKey;
        return;
      }
    }
  }, [
    monitor,
    templateValues,
    templateKey,
    form,
    initialSearchTerm,
    onMonitorNameChange,
    onSearchTermChange,
  ]);

  useEffect(() => {
    if (
      monitor === undefined &&
      templateValues?.searchTerm !== undefined &&
      form.getFieldValue("searchTerm") !== templateValues.searchTerm
    ) {
      form.setFieldsValue({
        searchTerm: templateValues.searchTerm ?? initialSearchTerm ?? "",
      });
    }
  }, [monitor, templateValues?.searchTerm, form, initialSearchTerm]);

  useEffect(() => {
    if (monitor !== undefined) {
      return;
    }
    if (templateValues?.label === undefined) {
      return;
    }

    const labelTouched = form.isFieldsTouched(["label"], true);
    if (!labelTouched && form.getFieldValue("label") !== templateValues.label) {
      form.setFieldsValue({ label: templateValues.label });
      onMonitorNameChange?.(templateValues.label);
    }
  }, [monitor, templateValues?.label, form, onMonitorNameChange]);

  const onSubmit = useCallback(
    async (data: MonitorFormData) => {
      setSaving(true);

      const submittedSearchTerm =
        typeof data.searchTerm === "string" ? data.searchTerm : undefined;
      const formSearchTerm = form.getFieldValue("searchTerm");
      const searchTermToValidate = submittedSearchTerm?.length
        ? submittedSearchTerm
        : typeof formSearchTerm === "string" && formSearchTerm.length > 0
          ? formSearchTerm
          : (monitor?.searchTerm ?? initialSearchTerm ?? "");

      const validationError = validateSearchTerm(searchTermToValidate);
      if (validationError) {
        form.setFields([
          {
            name: "searchTerm",
            value: form.getFieldValue("searchTerm") ?? searchTermToValidate,
            errors: [
              `Unable to save monitor: ${validationError}. Update the query and try again.`,
            ],
          },
        ]);
        setSaving(false);
        return;
      }

      if (data.searchTerm === undefined) {
        data.searchTerm = initialSearchTerm ?? "";
      }

      const updateAssignee = data.assignee?.trim()
        ? data.assignee
        : deleteField();

      try {
        const monitorCollection = collection(
          DB,
          `${toAssessmentPath(tenantId, assessmentId ?? "")}/monitors`
        );
        if (monitor) {
          const savedMonitor: SavedMonitor = {
            ...data,
            show,
          };
          if (data.searchTerm !== initialSearchTerm) {
            savedMonitor.hasBeenEvaluated = false;
          }
          const docRef = doc(monitorCollection, encodeURI(monitor.monitorId));
          if (!monitor.isCustom) {
            const presetBase = {
              priority: savedMonitor.priority,
              autoAssign: savedMonitor.autoAssign ?? false,
            };
            const docSnap = await getDoc(docRef);
            if (!docSnap.exists()) {
              await setDoc(docRef, {
                ...presetBase,
                ...(savedMonitor.assignee
                  ? { assignee: savedMonitor.assignee }
                  : {}),
              });
            } else {
              await updateDoc(docRef, {
                ...presetBase,
                assignee: updateAssignee,
              });
            }
          } else {
            await updateDoc(docRef, {
              ...savedMonitor,
              assignee: updateAssignee,
            });
          }
          message.success("Monitor updated");
        } else {
          const monitorData = pickBy(
            {
              ...data,
              show,
              archived: false,
              hasBeenEvaluated: false,
            },
            isDefined
          );
          const newMonitor = await addDoc(monitorCollection, monitorData);
          message.success("Monitor created");
          navigate(`${AppPaths.Posture}/monitors/${newMonitor.id}`, {}, true);
        }
        onSubmitForm();
        form.resetFields();
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "An error occurred while saving the monitor";
        message.error(errorMessage);
      } finally {
        setSaving(false);
      }
    },
    [
      assessmentId,
      onSubmitForm,
      form,
      tenantId,
      monitor,
      initialSearchTerm,
      show,
      navigate,
    ]
  );

  const autoAssignChanged = useCallback(
    (prev: { autoAssign: boolean }, curr: { autoAssign: boolean }) =>
      prev.autoAssign !== curr.autoAssign,
    []
  );
  const onFormValuesChange = useCallback(
    (
      changedValues: Partial<MonitorFormData>,
      _allValues: MonitorFormData
    ): void => {
      if ("label" in changedValues) {
        onMonitorNameChange?.(changedValues.label ?? "");
      }
    },
    [onMonitorNameChange]
  );
  return (
    <Form
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      onFinish={onSubmit}
      form={form}
      onValuesChange={onFormValuesChange}
    >
      <Form.Item
        label={hideSearchAndLabel ? "Monitor Name" : "Label"}
        name="label"
        rules={[{ required: true }]}
      >
        <Input
          disabled={!isCustom}
          placeholder={
            hideSearchAndLabel ? "Enter a name for this monitor" : undefined
          }
        />
      </Form.Item>
      <Form.Item
        label="Search Term"
        name="searchTerm"
        rules={[{ required: true }]}
      >
        <TextArea disabled={!isCustom} autoSize={{ minRows: 2, maxRows: 5 }} />
      </Form.Item>
      <Form.Item label="Show">
        <Input value={monitorContextLabel} readOnly disabled />
      </Form.Item>
      {!hideSearchAndLabel && <Divider />}
      <Form.Item label="Description" name="description" initialValue="">
        <Input.TextArea disabled={!isCustom} />
      </Form.Item>
      <Form.Item
        label="Priority"
        name="priority"
        rules={[{ required: true }]}
        initialValue="MEDIUM"
      >
        <Select>
          {monitorPriorities.map((priority) => (
            <Select.Option key={priority} value={priority}>
              {priorityLabels[priority]}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        label={
          <GraphTooltip
            title={"The integrations to which this monitor applies"}
          >
            Scope
          </GraphTooltip>
        }
        name="scopes"
        rules={[{ required: true }]}
      >
        <Select mode="multiple" disabled={!isCustom}>
          {monitorScopes.map((scope) => (
            <Select.Option key={scope} value={scope}>
              <>
                {scope !== ALL_SCOPE_SENTINEL && getTargetLogo(scope)}{" "}
                {integrationLabelFor(scope)}
              </>
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {jiraLoading ? (
        <Skeleton active paragraph={{ rows: 1 }} />
      ) : jiraDoc?.data?.state === "installed" ? (
        <>
          <Form.Item
            label={
              <>
                <GraphTooltip title={"Assign automatically with Jira"}>
                  Assign automatically {JiraIcon}
                </GraphTooltip>
              </>
            }
            name="autoAssign"
          >
            <Switch />
          </Form.Item>
          <Form.Item shouldUpdate={autoAssignChanged}>
            {({ getFieldValue }) =>
              getFieldValue("autoAssign") ? (
                <Form.Item
                  rules={[
                    {
                      type: "email",
                    },
                  ]}
                  label={
                    <>
                      <GraphTooltip
                        title={
                          "Select an assignee. If not specified, will default to assignment mechanism configured in settings."
                        }
                      >
                        Assignee Email
                      </GraphTooltip>
                    </>
                  }
                  name="assignee"
                >
                  <Input defaultValue={monitor?.assignee} />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </>
      ) : (
        <Flex style={{ marginBottom: 16 }} align="center">
          <Typography.Text>
            Save hours a week by automatically assigning monitors
          </Typography.Text>
          <Button
            type="link"
            style={{ textDecoration: "underline" }}
            href={`${AppPaths.Integrations}/jira`}
          >
            <ExportOutlined />
          </Button>{" "}
        </Flex>
      )}
      <Form.Item style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button type="primary" htmlType="submit" loading={saving}>
          {monitor ? "Submit" : "Create Monitor"}
        </Button>
      </Form.Item>
    </Form>
  );
};
